import { BaseApi } from "../BaseApi";

let API: BaseApi | null = null;

function getApiInstance(): BaseApi {
  if (!API) {
    API = new BaseApi();
  }
  return API;
}

function buildUrl(
  baseUrl: string,
  params?: { [key: string]: string | number }
) {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.keys(params).forEach((key) => {
      queryParams.append(key, params[key].toString());
    });
  }
  return `${baseUrl}?${queryParams.toString()}`;
}

// Any mutation on invoices/seller-expedition-fees busts every cached list
// response so the next fetch reflects the change instead of returning stale.
const invalidate = () => {
  BaseApi.invalidateCache("invoices");
  BaseApi.invalidateCache("seller-expedition-fees");
};
const withInvoiceInvalidate = <T>(p: Promise<T>): Promise<T> =>
  p.then((res) => {
    invalidate();
    return res;
  });

function getInvoicesSummary(params?: { owner?: string; country?: string; boutique?: string }) {
  const baseUrl = "invoices/summary";
  const clean: Record<string, string> = {};
  if (params?.owner) clean.owner = params.owner;
  if (params?.country) clean.country = params.country;
  if (params?.boutique) clean.boutique = params.boutique;
  const url = Object.keys(clean).length ? buildUrl(baseUrl, clean) : baseUrl;
  return getApiInstance().getCached(url);
}

function getInvoices(params: {
  limit: number;
  skip: number;
  keyword?: string;
  owner?: string;
  country?: string;
  status?: string;
  boutique?: string;
}) {
  const baseUrl = "invoices";
  // Strip empty/undefined to keep URLs clean
  const clean: Record<string, string | number> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") clean[k] = v as any;
  });
  const url = buildUrl(baseUrl, clean);
  return getApiInstance().getCached(url);
}

function deleteInvoiceApi(params: { ids: string[] }) {
  const baseUrl = "invoices";
  const url = `${baseUrl}/delete`;
  return withInvoiceInvalidate(getApiInstance().put(url, params));
}

function updateStatusInvoiceApi(params: { id: string; status?: string }) {
  const baseUrl = "invoices";
  const url = `${baseUrl}/status/${params.id}`;
  return withInvoiceInvalidate(
    getApiInstance().put(url, { status: params.status })
  );
}

function updateInvoiceApi(params: {
  id: string;
  expeditionFees: number;
  otherFees: number;
}) {
  const baseUrl = "invoices";
  const url = `${baseUrl}/${params.id}`;
  return withInvoiceInvalidate(getApiInstance().put(url, params));
}

function getInvoice(params: { id: string }) {
  const baseUrl = "invoices";
  const url = `${baseUrl}/${params.id}`;
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching Invoice:", error);
  }
}

function downloadInvoicePdf(params: { id: string; filename?: string }) {
  const url = `invoices/${params.id}/pdf`;
  const filename = params.filename || `facture-${params.id}.pdf`;
  try {
    return getApiInstance().downloadFile(url, filename);
  } catch (error) {
    console.error("Error downloading invoice PDF:", error);
  }
}

function sendInvoiceEmailApi(params: { id: string }) {
  const url = `invoices/${params.id}/send-email`;
  try {
    return getApiInstance().post(url, {});
  } catch (error) {
    console.error("Error sending invoice email:", error);
  }
}

function regenerateInvoiceApi(params: { id: string; apply?: boolean }) {
  const qs = params.apply ? "?apply=true" : "";
  const url = `invoices/${params.id}/regenerate${qs}`;
  try {
    return getApiInstance().post(url, {});
  } catch (error) {
    console.error("Error regenerating invoice:", error);
  }
}

function getPendingShipmentsForSellerApi(params: { sellerId: string; country?: string }) {
  const qs = params.country ? `?country=${encodeURIComponent(params.country)}` : "";
  const url = `invoices/admin/pending-shipments/${params.sellerId}${qs}`;
  try {
    return getApiInstance().get(url);
  } catch (error) {
    console.error("Error fetching pending shipments:", error);
  }
}

function createManualInvoiceApi(params: {
  sellerId: string;
  country: string;
  shipmentIds?: string[];
}) {
  return withInvoiceInvalidate(
    getApiInstance().post("invoices/admin/create-manual", params)
  );
}

function runPendingInvoicesApi() {
  return withInvoiceInvalidate(
    getApiInstance().post("invoices/admin/run-pending", {})
  );
}

function getExpeditionFeesList() {
  return getApiInstance().getCached("seller-expedition-fees/list");
}

function setExpeditionFee(params: { seller: string; amount: number }) {
  return withInvoiceInvalidate(
    getApiInstance().post("seller-expedition-fees", params)
  );
}

// Admin action: settle the seller's outstanding expedition fee OUTSIDE of an
// invoice (the seller paid directly). Backend zeros `amount`, sets `paidAt`,
// and leaves `paidByInvoice` null so the fees page can label it "Paid outside".
function markExpeditionFeePaidOutside(sellerId: string) {
  return withInvoiceInvalidate(
    getApiInstance().post(
      `seller-expedition-fees/${encodeURIComponent(sellerId)}/mark-paid-outside`,
      {}
    )
  );
}

export {
  getInvoices,
  getInvoicesSummary,
  getInvoice,
  deleteInvoiceApi,
  updateStatusInvoiceApi,
  updateInvoiceApi,
  downloadInvoicePdf,
  sendInvoiceEmailApi,
  regenerateInvoiceApi,
  getPendingShipmentsForSellerApi,
  createManualInvoiceApi,
  runPendingInvoicesApi,
  getExpeditionFeesList,
  setExpeditionFee,
  markExpeditionFeePaidOutside,
};
